import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import { FullPageLoader } from "@/components/app/kit";
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AcceptInvitation from "@/pages/auth/AcceptInvitation";
import PlatformLayout from "@/pages/platform/PlatformLayout";
import PlatformDashboard from "@/pages/platform/Dashboard";
import Tenants from "@/pages/platform/Tenants";
import TenantNew from "@/pages/platform/TenantNew";
import TenantDetail from "@/pages/platform/TenantDetail";
import TenantEdit from "@/pages/platform/TenantEdit";
import Modules from "@/pages/platform/Modules";
import Plans from "@/pages/platform/Plans";
import Domains from "@/pages/platform/Domains";
import Countries from "@/pages/platform/Countries";
import Admins from "@/pages/platform/Admins";
import Notifications from "@/pages/platform/Notifications";
import PlatformAudit from "@/pages/platform/Audit";
import PlatformSettings from "@/pages/platform/Settings";
import PlatformConfig from "@/pages/platform/PlatformConfig";
import Monitoring from "@/pages/platform/Monitoring";
import Subscription from "@/pages/tenant/Subscription";
import TenantLayout, { ModuleGate, ModuleRoute } from "@/pages/tenant/TenantLayout";
import TenantDashboard from "@/pages/tenant/Dashboard";
import Patients from "@/pages/tenant/Patients";
import TenantUsers from "@/pages/tenant/Users";
import Roles from "@/pages/tenant/Roles";
import TenantModules from "@/pages/tenant/Modules";
import ModulePage from "@/pages/tenant/ModulePage";
import TenantAudit from "@/pages/tenant/Audit";
import TenantSettings from "@/pages/tenant/Settings";
import PatientRecord from "@/pages/tenant/PatientRecord";
import Agenda from "@/pages/tenant/Agenda";
import ConsultationEditor, { ConsultationsList } from "@/pages/tenant/Consultations";
import Prescriptions from "@/pages/tenant/Prescriptions";
import Documents from "@/pages/tenant/Documents";
import Exams from "@/pages/tenant/Exams";
import Staff from "@/pages/tenant/Staff";
import Pricing from "@/pages/tenant/Pricing";
import Invoices from "@/pages/tenant/Invoices";
import Cash from "@/pages/tenant/Cash";
import Insurance from "@/pages/tenant/Insurance";
import Purchases from "@/pages/tenant/Purchases";
import Inventory from "@/pages/tenant/Inventory";
import Accounting from "@/pages/tenant/Accounting";
import Payroll from "@/pages/tenant/Payroll";
import FinanceDashboard from "@/pages/tenant/FinanceDashboard";

const Home = () => {
  const { me } = useAuth();
  if (me === null) return <FullPageLoader />;
  return <Navigate to={homeFor(me)} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser/:token" element={<ResetPassword />} />
          <Route path="/invitation/:token" element={<AcceptInvitation />} />
          <Route path="/admin" element={<PlatformLayout />}>
            <Route index element={<PlatformDashboard />} />
            <Route path="cabinets" element={<Tenants />} />
            <Route path="cabinets/nouveau" element={<TenantNew />} />
            <Route path="cabinets/:id" element={<TenantDetail />} />
            <Route path="cabinets/:id/modifier" element={<TenantEdit />} />
            <Route path="modules" element={<Modules />} />
            <Route path="formules" element={<Plans />} />
            <Route path="domaines" element={<Domains />} />
            <Route path="pays" element={<Countries />} />
            <Route path="administrateurs" element={<Admins />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="audit" element={<PlatformAudit />} />
            <Route path="parametres" element={<PlatformSettings />} />
            <Route path="configuration" element={<PlatformConfig />} />
            <Route path="supervision" element={<Monitoring />} />
          </Route>
          <Route path="/cabinet" element={<TenantLayout />}>
            <Route index element={<ModuleGate perm="dashboard:read"><TenantDashboard /></ModuleGate>} />
            <Route path="patients" element={<ModuleGate module="patients" perm="patients:read"><Patients /></ModuleGate>} />
            <Route path="patients/:id" element={<ModuleGate module="patients" perm="patients:read"><PatientRecord /></ModuleGate>} />
            <Route path="agenda" element={<ModuleGate module="agenda" perm="agenda:read"><Agenda /></ModuleGate>} />
            <Route path="consultations" element={<ModuleGate module="consultations" perm="consultations:read"><ConsultationsList /></ModuleGate>} />
            <Route path="consultations/:id" element={<ModuleGate module="consultations" perm="consultations:read"><ConsultationEditor /></ModuleGate>} />
            <Route path="ordonnances" element={<ModuleGate module="prescriptions" perm="prescriptions:read"><Prescriptions /></ModuleGate>} />
            <Route path="documents" element={<ModuleGate module="documents" perm="documents:read"><Documents /></ModuleGate>} />
            <Route path="examens" element={<ModuleGate module="exams" perm="exams:read"><Exams /></ModuleGate>} />
            <Route path="personnel" element={<ModuleGate module="staff" perm="staff:read"><Staff /></ModuleGate>} />
            <Route path="tarifs" element={<ModuleGate module="pricing" perm="pricing:read"><Pricing /></ModuleGate>} />
            <Route path="factures" element={<ModuleGate module="billing" perm="billing:read"><Invoices /></ModuleGate>} />
            <Route path="caisse" element={<ModuleGate module="cash" perm="cash:read"><Cash /></ModuleGate>} />
            <Route path="assurances" element={<ModuleGate module="insurance" perm="insurance:read"><Insurance /></ModuleGate>} />
            <Route path="achats" element={<ModuleGate module="purchases" perm="purchases:read"><Purchases /></ModuleGate>} />
            <Route path="stocks" element={<ModuleGate module="inventory" perm="inventory:read"><Inventory /></ModuleGate>} />
            <Route path="comptabilite" element={<ModuleGate module="accounting" perm="accounting:read"><Accounting /></ModuleGate>} />
            <Route path="remunerations" element={<ModuleGate module="hr" perm="hr:read"><Payroll /></ModuleGate>} />
            <Route path="finances" element={<ModuleGate module="finance" perm="finance:read"><FinanceDashboard /></ModuleGate>} />
            <Route path="utilisateurs" element={<ModuleGate perm="users:read"><TenantUsers /></ModuleGate>} />
            <Route path="roles" element={<ModuleGate perm="users:read"><Roles /></ModuleGate>} />
            <Route path="modules" element={<TenantModules />} />
            <Route path="m/:code" element={<ModuleRoute />}>
              <Route index element={<ModulePage />} />
            </Route>
            <Route path="audit" element={<ModuleGate perm="audit:read"><TenantAudit /></ModuleGate>} />
            <Route path="parametres" element={<ModuleGate perm="settings:read"><TenantSettings /></ModuleGate>} />
            <Route path="abonnement" element={<ModuleGate perm="settings:read"><Subscription /></ModuleGate>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
